"use client";

import { forwardRef, useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { cn } from "@/lib/utils";
import Button from "./Button";
import type { InputProps } from "@/types";

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      showPasswordToggle = false,
      containerClassName = "",
      inputClassName = "",
      errorClassName = "",
      labelClassName = "",
      rightIcon,
      onRightIconClick,
      type = "text",
      className,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    
    const actualType = showPasswordToggle && type === "password" 
      ? (showPassword ? "text" : "password") 
      : type;
      

    const getDefaultInputClasses = () => {
      if (inputClassName) return inputClassName;
      
      const baseClasses = "w-full border-b border-gray-300 px-3 py-4 text-lg md:text-xl focus:outline-none focus:border-brand-blue";
      const readOnlyClasses = props.readOnly ? "bg-transparent text-gray-700 cursor-default" : "";
      const rightPadding = (rightIcon || (showPasswordToggle && type === "password")) ? "pr-12" : "";
      
      return cn(
        baseClasses,
        readOnlyClasses,
        rightPadding,
        inputClassName
      );
    };

    const getDefaultErrorClasses = () => {
      if (errorClassName) return errorClassName;
      return "text-red-500 text-sm mt-1";
    };

    const getDefaultLabelClasses = () => {
      if (labelClassName) return labelClassName;
      return "block text-base font-bold text-gray-700 mb-2";
    };

    return (
      <div className={containerClassName}>
        {label && (
          <label className={getDefaultLabelClasses()}>
            {label}
          </label>
        )}
        
        <div className="relative">
          <input
            ref={ref}
            type={actualType}
            className={cn(
              getDefaultInputClasses(),
              showPasswordToggle && type === "password" && "pr-10",
              className
            )}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            {...props}
          />
          
          {showPasswordToggle && type === "password" && (
            <Button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              variant="ghost"
              size="sm"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              tabIndex={-1}
            >
              {showPassword ? (
                <MdVisibilityOff className="w-5 h-5" />
              ) : (
                <MdVisibility className="w-5 h-5" />
              )}
            </Button>
          )}
          
          {rightIcon && !showPasswordToggle && (
            <Button
              type="button"
              onClick={onRightIconClick}
              variant="ghost"
              size="sm"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              tabIndex={-1}
            >
              {rightIcon}
            </Button>
          )}
        </div>
        
        {error && (
          <p className={getDefaultErrorClasses()}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
